
import React, { useCallback, useState, useEffect } from 'react';
import { fileHandler, mapRowData } from 'utils/excelRenderer';
import { useDropzone } from 'react-dropzone';
import { enqueueSnackbar } from 'notistack';
import { exportToCSV } from 'components/Common/CSVManage';
import { toBase64 } from 'utils/fileHelper';
import { useLocation } from 'react-router-dom';
import { initAxios } from 'utils/axiosHelper';

interface value {
  searchVal: string,
  type: string,
}
export interface automationParams {
  values?: value[];
  entState?: IEntityState;
  comState?: ICommodityState;
  row?: string;
}

interface IEntityState {
  fuzzy: boolean,
  dateRange: string[],
  lenses: string[],
  countries: string[],
  agencies: string[],
  lists: string[],
}

interface ICommodityState {
  fuzzy: boolean,
  dateRange: string[],
  comLenses: string[],
  countries: string[],
  agencies: string[],
  lists: string[],
}


const SupplierQuestionnaireBlur: React.FC = () => {
  // Automation Building State
  const [ complete, setComplete ] = useState(false);
  const [ noCase, setNoCase ] = useState(false);
  const [ file, setFile ] = useState<any>(undefined);
  const [ rows, setRows ] = useState<any>([]);
  const [ cols, setCols ] = useState<any>([]);
  const [ loading, setLoading ] = useState(false);
  const [ rowData, setRowData ] = useState([]);
  const [ cur_case, setCase ] = useState<{
        isLoaded: boolean;
        error: string | undefined;
        data: any;
      }>({ isLoaded: false, error: undefined, data: {} });

  const getLastPathSegment = (input: string): string => {
    return input.split('/').pop() || '';
  };

  const location = useLocation();

  const { pathname } = location;
  const ticket_id: any = getLastPathSegment(pathname);


  const template = [ 'Name of Facility',	'Address of Facility',	'Contact Name',	'Contact Title',	'Parent Company', 'Facility Owners',	'Subcontractors (If Applicable)' ];

  const entityHeaders = [
    'Importer of Record Name',
    'Importer of Record',
    'Exporter Name',
    'Buyer Name',
    'Seller Name',
    'Manufacturer Name',
    'Ship To Party',
    'Consolidator (Stuffer) Name',
    'Vessel Name',
    'Carrier\'s Information',
    'Company Legal Name',
    'Parent Company (If applicable)',
    'Company Identification Number',
    'Contact Person Information',
    'Company Website',
    'Zone Approval Officer',
    'Vessel Name or Aircraft',
    'Name of Facility',
    'Contact Name',
    'Parent Company',
    'Facility Owners',
    'Subcontractors (If Applicable)',
    'Customs Broker Name',
    'Carrier Contact Name',
    'Carrier Name',
    'Consignee Contact Name',
    'Consignee Company Name',
    'Consignee Address',
    'Shipper Email',
    'Shipper Contact Name',
    'Shipper Company Name',
    'Shipper Address',

  ];
  const codeHeaders = [
    'Commodity Harmonized Tariff Schedule Number (HTS)',
    'HS Code',
    'HS Code(s)',
    'Item'
  ];
  const descHeaders = [
    'Product Detailed Information',
    'Description of Merchandise',
    'Item',
    'Assembly Name or Number',
    'Sub Assembly Name or Number',
    'Component / Part Name',
    'Part Number',
    'Description',
    'Description of Goods'
  ];
  const countryHeaders = [
    'Country of Origin'
  ];

  const mapType = (header: string) => {
    if (entityHeaders.includes(header)) {
      return 'entity';
    } else if (codeHeaders.includes(header)) {
      return 'code';
    } else if (descHeaders.includes(header)) {
      return 'desc';
    } else if (countryHeaders.includes(header)) {
      return 'country';
    }
    return 'exclude';
  };


  const compareHeaders = (arr1: string[]) => {
    const sortedArr1 = arr1.slice().sort();
    const sortedArr2 = template.slice().sort();

    return sortedArr1.every((value, index) => value === sortedArr2[index]);
  };


  const onDrop = useCallback((acceptedFiles: File[]) => {
    acceptedFiles.forEach(async(accptFile) => {
      const reader = new FileReader();
      reader.onabort = () => {
        enqueueSnackbar('File reading aborted', { variant: 'info' });
      };
      reader.onerror = () => {
        enqueueSnackbar('Error reading file', { variant: 'error' });
      };
      setFile(accptFile);
      const { rows } = await fileHandler(accptFile);
      const colNames: string[] = rows[0];
      const tempCheck = compareHeaders(colNames);
      if (tempCheck) {
        const rowData = rows.slice(1);
        const mappedRowData = mapRowData(colNames, rowData);
        setRows(mappedRowData);
        setCols(colNames);
      } else {
        enqueueSnackbar('Your file does not match your selected template. Please try again with the correct format', { variant: 'error' });
        setFile(undefined);
      }
    });
  }, []);

  const fileValidator = (file: any) => {
    if (file.size > 20000000) {
      enqueueSnackbar('Your file is too large, upload a file less than 20mbs', { variant: 'error' });
      return {
        code: 'size-too-large',
        message: 'file is larger than 20MB',
      };
    }
    return null;
  };
  const removeFile = () => {
    setFile(undefined);
    setRows([]);
    setCols([]);
  };
  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    onDrop: onDrop,
    validator: fileValidator,
    accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet , text/csv',
  });

  const handleTicket = async(values: any) => {
    if (cur_case) {
      const update = {
        _id: cur_case.data._id,
        ...values.basics,
        ...values,
      };
      const axiosInstance = await initAxios(undefined, false); // No auth required
      if (!axiosInstance) {
        const error_msg = 'Error updating ticket. Could not connect to the BITE database';
        enqueueSnackbar(error_msg, { variant: 'error' });
        return;
      };
      try {
        await axiosInstance.put('/cases', JSON.stringify(update),);
        enqueueSnackbar('Successfully updated ticket you may now close this window', { variant: 'success' });
        setLoading(false);
        setComplete(true);
      } catch (error) {
        const err_text = `Error loading ticket: ${error}`;
        enqueueSnackbar(err_text, { variant: 'error' });
      }
    }
  };

  const replaceOrReturn = (arr: any, newObj: any): any[] => {
    if (arr.length > 0) {
      arr[0] = newObj;
      return arr;
    }
    return [ newObj ];
  };


  const saveFile = async() => {
    setLoading(true);
    let fileInfo = {};
    const base64Str = await toBase64(file);
    fileInfo = {
      uploadedFile: {
        base64: base64Str,
        name: file?.name,
        dateAdded: Date.now(),
        source: true
      },
    };
    const actObj: any = {
      user: {
        kc_id: '',
        email: '',
        firstName: '',
        lastName: '',
      },
      action: `supplier added the document ${file?.name} to the ticket for screening`,
      timeStamp: Date.now()
    };

    const query = {
      documents: cur_case?.data?.documents ? replaceOrReturn(cur_case?.data?.documents, fileInfo) : fileInfo,
      status: 'Complete',
      supQuestReady: true,
      activity: [ ...cur_case.data.activity, actObj ],
    };

    handleTicket(query);
  };

  useEffect(() => {
    if (ticket_id) {
      const getCase = async() => {
        const axiosInstance = await initAxios(undefined, false); // No auth required
        if (!axiosInstance) {
          return;
        };
        try {
          const res = await axiosInstance.get(`/cases/${ticket_id}`);
          if (!res.data) {
            setNoCase(true);
            const err_text = 'No ticket found';
            enqueueSnackbar(err_text, { variant: 'error' });
          }
          setCase({ isLoaded: true, error: undefined, data: res.data });
        } catch (error) {
          const err_text = `Error loading case: ${error}`;
          enqueueSnackbar(err_text, { variant: 'error' });
        }
      };

      getCase();
    }
  }, [ ticket_id ]);

  const valsArrofObjs: any = [];

  useEffect(() => {
    if (rows.length > 0) {
      rows.forEach((row: any, i: number) => {
        Object.keys(row).forEach((key) => {
          const obj = {
            type: mapType(key),
            searchVal: row[key],
            row: i
          };
          if (obj.type) {
            valsArrofObjs.push(obj);
          }
        });
      });
      setRowData(valsArrofObjs);
    } else {
      setRowData([]);
    }
  }, [ rows ]);

  return (
    <div className='bill-row' style={{ marginBottom: '100px' }}>
      <div className='bill-container'>
        <div className='bill-container-header'>
          <div className='bill-container-header-bread-crumbs'>
            <div className='bill-container-header-bread-crumbs-tabs'>
              <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <g id='dataflow-03' clipPath='url(#clip0_4246_1805)'>
                  <path id='Icon' d='M9.16675 3.75008H15.2501C16.1835 3.75008 16.6502 3.75008 17.0067 3.93174C17.3203 4.09153 17.5753 4.34649 17.7351 4.6601C17.9167 5.01662 17.9167 5.48333 17.9167 6.41675V7.50008C17.9167 8.27665 17.9167 8.66494 17.7899 8.97122C17.6207 9.3796 17.2963 9.70406 16.8879 9.87321C16.5816 10.0001 16.1933 10.0001 15.4167 10.0001M10.8334 16.2501H4.75008C3.81666 16.2501 3.34995 16.2501 2.99343 16.0684C2.67983 15.9086 2.42486 15.6537 2.26507 15.3401C2.08341 14.9835 2.08341 14.5168 2.08341 13.5834V12.5001C2.08341 11.7235 2.08341 11.3352 2.21028 11.0289C2.37944 10.6206 2.7039 10.2961 3.11228 10.1269C3.41856 10.0001 3.80685 10.0001 4.58341 10.0001M8.58342 12.0834H11.4167C11.6501 12.0834 11.7668 12.0834 11.8559 12.038C11.9343 11.9981 11.9981 11.9343 12.038 11.8559C12.0834 11.7668 12.0834 11.6501 12.0834 11.4167V8.58342C12.0834 8.35006 12.0834 8.23338 12.038 8.14425C11.9981 8.06585 11.9343 8.00211 11.8559 7.96216C11.7668 7.91675 11.6501 7.91675 11.4167 7.91675H8.58342C8.35006 7.91675 8.23338 7.91675 8.14425 7.96216C8.06585 8.00211 8.00211 8.06585 7.96216 8.14425C7.91675 8.23338 7.91675 8.35006 7.91675 8.58342V11.4167C7.91675 11.6501 7.91675 11.7668 7.96216 11.8559C8.00211 11.9343 8.06585 11.9981 8.14425 12.038C8.23338 12.0834 8.35006 12.0834 8.58342 12.0834ZM14.8334 18.3334H17.6667C17.9001 18.3334 18.0168 18.3334 18.1059 18.288C18.1843 18.2481 18.2481 18.1843 18.288 18.1059C18.3334 18.0168 18.3334 17.9001 18.3334 17.6667V14.8334C18.3334 14.6001 18.3334 14.4834 18.288 14.3943C18.2481 14.3159 18.1843 14.2521 18.1059 14.2122C18.0168 14.1667 17.9001 14.1667 17.6667 14.1667H14.8334C14.6001 14.1667 14.4834 14.1667 14.3943 14.2122C14.3159 14.2521 14.2521 14.3159 14.2122 14.3943C14.1667 14.4834 14.1667 14.6001 14.1667 14.8334V17.6667C14.1667 17.9001 14.1667 18.0168 14.2122 18.1059C14.2521 18.1843 14.3159 18.2481 14.3943 18.288C14.4834 18.3334 14.6001 18.3334 14.8334 18.3334ZM2.33341 5.83341H5.16675C5.4001 5.83341 5.51678 5.83341 5.60591 5.788C5.68431 5.74805 5.74805 5.68431 5.788 5.60591C5.83341 5.51678 5.83341 5.4001 5.83341 5.16675V2.33341C5.83341 2.10006 5.83341 1.98338 5.788 1.89425C5.74805 1.81585 5.68431 1.75211 5.60591 1.71216C5.51678 1.66675 5.4001 1.66675 5.16675 1.66675H2.33341C2.10006 1.66675 1.98338 1.66675 1.89425 1.71216C1.81585 1.75211 1.75211 1.81585 1.71216 1.89425C1.66675 1.98338 1.66675 2.10006 1.66675 2.33341V5.16675C1.66675 5.4001 1.66675 5.51678 1.71216 5.60591C1.75211 5.68431 1.81585 5.74805 1.89425 5.788C1.98338 5.83341 2.10006 5.83341 2.33341 5.83341Z' stroke='#4895EF' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
                </g>
                <defs>
                  <clipPath id='clip0_4246_1805'>
                    <rect width='20' height='20' fill='white'/>
                  </clipPath>
                </defs>
              </svg>
              <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                <path d='M6 12L10 8L6 4' stroke='#BDBEC1' strokeWidth='1.33333' strokeLinecap='round' strokeLinejoin='round'/>
              </svg>
              <p>Monitor</p>

              <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                <path d='M6 12L10 8L6 4' stroke='#BDBEC1' strokeWidth='1.33333' strokeLinecap='round' strokeLinejoin='round'/>
              </svg>
              <p>...</p>
              <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                <path d='M6 12L10 8L6 4' stroke='#BDBEC1' strokeWidth='1.33333' strokeLinecap='round' strokeLinejoin='round'/>
              </svg>
              <p>{cur_case.data?.ticketName ? cur_case.data.ticketName : 'New Ticket'}</p>
            </div>
          </div>
        </div>
        <div className='bill-container-body'>
          <div className={'bill-container-body-row-blur'}
          >
            <div className='bill-container-body-row-avatar'>
              <svg width='18' height='19' viewBox='0 0 18 19' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <g id='upload-03' clipPath='url(#clip0_4002_972)'>
                  <path id='Icon' d='M12 9.1875L9 6.1875M9 6.1875L6 9.1875M9 6.1875V12.1875M16.5 9.1875C16.5 13.3296 13.1421 16.6875 9 16.6875C4.85786 16.6875 1.5 13.3296 1.5 9.1875C1.5 5.04536 4.85786 1.6875 9 1.6875C13.1421 1.6875 16.5 5.04536 16.5 9.1875Z' stroke='#4895EF' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                </g>
                <defs>
                  <clipPath id='clip0_4002_972'>
                    <rect width='18' height='18' fill='white' transform='translate(0 0.1875)'/>
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div className='bill-container-body-row-frame'>
              <h1>Step 1: Ticket Details</h1>
            </div>
          </div>
          <div className='bill-container-body-row-line'>
            <div className='bill-container-body-row-line-icon'>
              <svg xmlns='http://www.w3.org/2000/svg' width='2' height='12' viewBox='0 0 2 12' fill='none'>
                <path d='M1 0V12' stroke='#BDBEC1' strokeWidth='2'/>
              </svg>
            </div>
          </div>
          <div className={'bill-container-body-row-blur'}
          >
            <div className= 'bill-container-body-row-avatar'>

              <svg xmlns='http://www.w3.org/2000/svg' width='18' height='19' viewBox='0 0 18 19' fill='none'>
                <path d='M7.87532 9.18759H3.75032M3.68683 9.40621L1.93564 14.6372C1.79806 15.0482 1.72928 15.2537 1.77864 15.3802C1.82151 15.4901 1.91358 15.5734 2.02719 15.6051C2.15802 15.6416 2.35562 15.5527 2.75082 15.3749L15.2844 9.73474C15.6702 9.56116 15.863 9.47436 15.9227 9.35379C15.9744 9.24904 15.9744 9.12613 15.9227 9.02138C15.863 8.90081 15.6702 8.81402 15.2844 8.64044L2.74645 2.99838C2.35244 2.82108 2.15544 2.73243 2.02474 2.76879C1.91124 2.80037 1.81917 2.88346 1.77616 2.99315C1.72662 3.11944 1.79468 3.32447 1.93078 3.73454L3.68732 9.02673C3.71069 9.09716 3.72238 9.13238 3.72699 9.16839C3.73109 9.20035 3.73105 9.23271 3.72687 9.26465C3.72216 9.30066 3.71039 9.33584 3.68683 9.40621Z' stroke='#4895EF' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
              </svg>
            </div>
            <div className='bill-container-body-row-frame'>
              <h1>Step 2: Send to</h1>
            </div>
          </div>
          <div className='bill-container-body-row-line'>
            <div className='bill-container-body-row-line-icon'>
              <svg xmlns='http://www.w3.org/2000/svg' width='2' height='12' viewBox='0 0 2 12' fill='none'>
                <path d='M1 0V12' stroke='#BDBEC1' strokeWidth='2'/>
              </svg>
            </div>
          </div>
          <div className={'bill-container-body-row selected'}
          >
            <div className='bill-container-body-row-avatar'>
              <svg xmlns='http://www.w3.org/2000/svg' width='18' height='19' viewBox='0 0 18 19' fill='none'>
                <g clipPath='url(#clip0_26012_8210)'>
                  <path d='M12 9.1875L9 6.1875M9 6.1875L6 9.1875M9 6.1875V12.1875M16.5 9.1875C16.5 13.3296 13.1421 16.6875 9 16.6875C4.85786 16.6875 1.5 13.3296 1.5 9.1875C1.5 5.04536 4.85786 1.6875 9 1.6875C13.1421 1.6875 16.5 5.04536 16.5 9.1875Z' stroke='#4895EF' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                </g>
                <defs>
                  <clipPath id='clip0_26012_8210'>
                    <rect width='18' height='18' fill='white' transform='translate(0 0.1875)'/>
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div className='bill-container-body-row-frame'>
              <h1>Step 3: Re-upload questionnaire</h1>
            </div>
          </div>
          <div className='bill-container-body-row-line'>
            <div className='bill-container-body-row-line-icon'>
              <svg xmlns='http://www.w3.org/2000/svg' width='2' height='12' viewBox='0 0 2 12' fill='none'>
                <path d='M1 0V12' stroke='#BDBEC1' strokeWidth='2'/>
              </svg>
            </div>
          </div>
          <div className={'bill-container-body-row-blur'}

          >
            <div className='bill-container-body-row-avatar'>

              <svg xmlns='http://www.w3.org/2000/svg' width='18' height='19' viewBox='0 0 18 19' fill='none'>
                <path d='M6 6.1875V5.4375M6 9.5625V8.8125M6 12.9375V12.1875M5.1 15.1875H12.9C14.1601 15.1875 14.7902 15.1875 15.2715 14.9423C15.6948 14.7265 16.039 14.3823 16.2548 13.959C16.5 13.4777 16.5 12.8476 16.5 11.5875V6.7875C16.5 5.52738 16.5 4.89732 16.2548 4.41602C16.039 3.99266 15.6948 3.64845 15.2715 3.43274C14.7902 3.1875 14.1601 3.1875 12.9 3.1875H5.1C3.83988 3.1875 3.20982 3.1875 2.72852 3.43274C2.30516 3.64845 1.96095 3.99266 1.74524 4.41602C1.5 4.89732 1.5 5.52738 1.5 6.7875V11.5875C1.5 12.8476 1.5 13.4777 1.74524 13.959C1.96095 14.3823 2.30516 14.7265 2.72852 14.9423C3.20982 15.1875 3.83988 15.1875 5.1 15.1875Z' stroke='#4895EF' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
              </svg>
            </div>
            <div className='bill-container-body-row-frame'>
              <h1>Step 4: Screening Parameters</h1>
            </div>
          </div>
          <div className='bill-container-body-row-line'>
            <div className='bill-container-body-row-line-icon'>
              <svg xmlns='http://www.w3.org/2000/svg' width='2' height='12' viewBox='0 0 2 12' fill='none'>
                <path d='M1 0V12' stroke='#BDBEC1' strokeWidth='2'/>
              </svg>
            </div>
          </div>
          <div className={'bill-container-body-row-blur'}

          >
            <div className='bill-container-body-row-avatar'>

              <svg xmlns='http://www.w3.org/2000/svg' width='18' height='19' viewBox='0 0 18 19' fill='none'>
                <path d='M7.87532 9.18759H3.75032M3.68683 9.40621L1.93564 14.6372C1.79806 15.0482 1.72928 15.2537 1.77864 15.3802C1.82151 15.4901 1.91358 15.5734 2.02719 15.6051C2.15802 15.6416 2.35562 15.5527 2.75082 15.3749L15.2844 9.73474C15.6702 9.56116 15.863 9.47436 15.9227 9.35379C15.9744 9.24904 15.9744 9.12613 15.9227 9.02138C15.863 8.90081 15.6702 8.81402 15.2844 8.64044L2.74645 2.99838C2.35244 2.82108 2.15544 2.73243 2.02474 2.76879C1.91124 2.80037 1.81917 2.88346 1.77616 2.99315C1.72662 3.11944 1.79468 3.32447 1.93078 3.73454L3.68732 9.02673C3.71069 9.09716 3.72238 9.13238 3.72699 9.16839C3.73109 9.20035 3.73105 9.23271 3.72687 9.26465C3.72216 9.30066 3.71039 9.33584 3.68683 9.40621Z' stroke='#4895EF' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
              </svg>
            </div>
            <div className='bill-container-body-row-frame'>
              <h1>Step 5: Share with (Optional)</h1>
            </div>
          </div>
        </div>
      </div>
      <div className='bill-container-fit-screen'>
        <div className='bill-container-fit-screen-header'>
          <div className='bill-container-fit-screen-header-p'>
            <p>{noCase ? 'This Ticket Has Been Deleted from BITE' : cur_case.data?.supQuestReady ? 'Questionnaire Already Submitted' : 'Upload Supplier Questionnaire'}</p>
          </div>
        </div>
        {(!cur_case.data?.supQuestReady && !noCase) && <div className='bill-container-fit-screen-content-row'>
          <div className='bill-container-fit-screen-content-row-btn' onClick={() => exportToCSV('Supplier Questionnaire', [], template)}>
            <div className='bill-container-fit-screen-content-row-btn-text'>
              <svg xmlns='http://www.w3.org/2000/svg' width='19' height='18' viewBox='0 0 19 18' fill='none'>
                <path d='M6.5 9L9.5 12M9.5 12L12.5 9M9.5 12V6M17 9C17 13.1421 13.6421 16.5 9.5 16.5C5.35786 16.5 2 13.1421 2 9C2 4.85786 5.35786 1.5 9.5 1.5C13.6421 1.5 17 4.85786 17 9Z' stroke='#E5F2FD' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
              </svg>
              <p>Download Questionnaire</p>
            </div>
          </div>
        </div>}
        {(!cur_case.data?.supQuestReady && !noCase) && <div className='bill-container-fit-screen-content-row'>
          <div className='bill-container-fit-screen-content-col'>
            <div className='bill-container-fit-screen-content-col-h'> CSV Upload<span>*</span></div>
            <div className='bill-container-fit-screen-content-col-text'>
                Fill out the questionnaire and upload here.
            </div>
          </div>
          <div className='bill-container-fit-screen-content-csv-upload'>
            <div className='bill-container-fit-screen-content-csv-upload-icon'>
              <svg width='52' height='64' viewBox='0 0 52 64' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <g id='Page'>
                  <path id='Page background' d='M0.950195 4C0.950195 2.20507 2.40527 0.75 4.2002 0.75H32.2002C32.5135 0.75 32.814 0.874466 33.0355 1.09601L50.3042 18.3646C50.5257 18.5862 50.6502 18.8867 50.6502 19.2V60C50.6502 61.7949 49.1951 63.25 47.4002 63.25H4.2002C2.40527 63.25 0.950195 61.7949 0.950195 60V4Z' stroke='#37393F' strokeWidth='1.5'/>
                  <path id='Earmark' d='M32.2002 0.799805V15.1998C32.2002 17.4089 33.9911 19.1998 36.2002 19.1998H50.6002' stroke='#37393F' strokeWidth='1.5'/>
                </g>
              </svg>
              <div className='bill-container-fit-screen-content-csv-upload-icon-text'>
                XLS
              </div>
            </div>
            <div className='bill-container-fit-screen-content-csv-upload-file' {...getRootProps()}>
              <div className={`bill-container-fit-screen-content-csv-upload-file-card ${file?.name ? 'selected-file' : ''}`}>
                <div className='bill-container-fit-screen-content-csv-upload-file-card-cont'>
                  <div className='bill-container-fit-screen-content-csv-upload-file-card-cont-text'>
                    { !file?.name ? (
                      <>
                        <div className='bill-container-fit-screen-content-csv-upload-file-card-cont-text-action'>
                          <span>Click to upload </span>or drag and drop</div>
                        <div className='bill-container-fit-screen-content-csv-upload-file-card-cont-text-sub'>
                        CSV file format only (max 200 MB)
                        </div>
                        <input {...getInputProps()} />
                      </>
                    ) : (
                      <>
                        <div className='bill-container-fit-screen-content-csv-upload-file-card-cont-text-action'>
                          <span>{file?.name} </span>
                          <svg onClick={removeFile} width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <g id='trash-03'>
                              <path id='Icon' d='M7.5 2.5H12.5M2.5 5H17.5M15.8333 5L15.2489 13.7661C15.1612 15.0813 15.1174 15.7389 14.8333 16.2375C14.5833 16.6765 14.206 17.0294 13.7514 17.2497C13.235 17.5 12.5759 17.5 11.2578 17.5H8.74221C7.42409 17.5 6.76503 17.5 6.24861 17.2497C5.79396 17.0294 5.41674 16.6765 5.16665 16.2375C4.88259 15.7389 4.83875 15.0813 4.75107 13.7661L4.16667 5M8.33333 8.75V12.9167M11.6667 8.75V12.9167' stroke='#BDBEC1' strokeWidth='1.66667' strokeLinecap='round' strokeLinejoin='round'/>
                            </g>
                          </svg>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
        <div className='bill-container-footer'>
          {rowData.length > 0 && !complete ?
            <div className='bill-container-footer-btn-submit-active' onClick={saveFile}>
              <div className='bill-container-footer-btn-text-active'>
                    Submit
              </div>
            </div> : <div className='bill-container-footer-btn-submit'>
              <div className='bill-container-footer-btn-text'>
                    Submit
              </div>
            </div>}
        </div>
      </div>
    </div>
  );
};

export default SupplierQuestionnaireBlur;
